import { useState, useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { SearchInterface } from "@/components/SearchInterface";
import { SearchResults } from "@/components/SearchResults";
import { Moved } from "@/components/Retired";
import { ChangelogModal } from "@/components/ChangelogModal";
import { DiscountOfferBanner } from "@/components/DiscountOfferBanner";
import { TutorialOverlay, SemanticSearchCallout, WordAddinModal, TutorialCompletionModal } from "@/components/tutorial";
import { useAuth } from "@/hooks/useAuth";
import { useTutorial } from "@/hooks/useTutorial";
import { CHANGELOG_VERSION } from "@/constants/changelog";
import { getTrialStatus, getABTestStatus } from "@/lib/utils";

interface Doctrina {
  id: number;
  texto: string;
}
interface SearchResult {
  fallo_id?: number;
  texto: string;
  metadata: {
    id: number;
    fallo_id: number;
    tribunal?: string;
    expediente: string;
    caratula?: string;
    autos?: string;
    fecha: string;
    link: string;
    tomo?: number;
    pagina?: number;
  };
  doctrinas: Doctrina[];
}

const Index = () => {
  const { user, loading, subscription, loadingSubscription } = useAuth();
  const { currentStep, progress } = useTutorial();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [currentCollection, setCurrentCollection] = useState<string>("");
  const [showSubscription, setShowSubscription] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("objetos");
  const [showChangelog, setShowChangelog] = useState(false);
  const [filterTerms, setFilterTerms] = useState<string[]>([]);
  const [showWordAddinModal, setShowWordAddinModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const searchInterfaceRef = useRef<{
    searchDoctrina: (text: string, collection: string) => Promise<void>;
    searchObjeto: (text: string, collection: string) => Promise<void>;
    getActiveTab: () => string;
  }>(null);

  // Handle router state from history navigation
  useEffect(() => {
    if (location.state?.searchResults && location.state?.fromHistory) {
      setSearchResults(location.state.searchResults);
      setCurrentCollection(location.state?.collection || "");
    }
  }, [location.state]);

  // Check if changelog should be shown
  useEffect(() => {
    if (user && !loading) {
      const changelogKey = `changelog-dismissed-v${CHANGELOG_VERSION}`;
      const isDismissed = localStorage.getItem(changelogKey);
      if (!isDismissed) {
        setShowChangelog(true);
      }
    }
  }, [user, loading]);

  // Show Word Add-in modal when reaching that tutorial step
  useEffect(() => {
    if (currentStep === "word_addin") {
      setShowWordAddinModal(true);
    }
  }, [currentStep]);

  // Show completion modal when tutorial is completed
  useEffect(() => {
    if (progress?.tutorial_completed && !progress?.tutorial_dismissed) {
      setShowCompletionModal(true);
    }
  }, [progress?.tutorial_completed, progress?.tutorial_dismissed]);

  const handleResults = (results: SearchResult[], collection: string) => {
    setSearchResults(results);
    setCurrentCollection(collection);
  };

  const handleBackToSearch = () => {
    setShowSubscription(false);
  };

  const handleSearchDoctrina = (text: string, collection: string) => {
    if (searchInterfaceRef.current?.searchDoctrina) {
      searchInterfaceRef.current.searchDoctrina(text, collection);
    }
  };

  const handleSearchObjeto = (text: string, collection: string) => {
    if (searchInterfaceRef.current?.searchObjeto) {
      searchInterfaceRef.current.searchObjeto(text, collection);
    }
  };

  // Check A/B test status
  const { isDiscountGroup, discountActive } = getABTestStatus(subscription);
  
  // Only show upgrade callout for free plan users
  const showUpgradeCallout = !loadingSubscription && subscription?.plan === "free";
  
  // Show discount banner for discount group, trial callout for trial group
  // Retired build: no upgrade offers; plans live in the new app.
  const showDiscountBanner = false;
  const showTrialCallout = false;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading DoctIA...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  return (
    <>
      <Helmet>
        <title>DoctIA | Buscar</title>
        <meta name="description" content="Busca jurisprudencia y precedentes legales con inteligencia artificial" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header onShowSubscription={() => setShowSubscription(true)} onShowChangelog={() => setShowChangelog(true)} />

        <div className="container max-w-4xl mx-auto px-6 py-8">
          {showSubscription ? (
            <Moved path="/suscripcion" />
          ) : (
            <div className="space-y-8">
              {/* Discount Banner for discount group */}
              {showDiscountBanner && <DiscountOfferBanner variant="full" />}

              {/* Trial/Free Plan Upgrade Callout for trial group */}
              {showTrialCallout &&
                (() => {
                  const { isTrialActive, isTrialExpired, daysLeft } = getTrialStatus(subscription);

                  return (
                    <div
                      onClick={() => navigate("/planes")}
                      className="cursor-pointer rounded-lg border border-primary/20 bg-primary/5 p-5 transition-colors hover:bg-primary/10"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-xs font-medium text-primary uppercase tracking-wide mb-1">
                            {isTrialExpired
                              ? "Tu período de prueba ha expirado"
                              : isTrialActive
                                ? `Te quedan ${daysLeft} día${daysLeft !== 1 ? "s" : ""} de prueba`
                                : "Comenzá a usar DoctIA"}
                          </p>
                          <p className="text-sm text-foreground">
                            {isTrialExpired
                              ? "Suscribite para continuar accediendo a búsquedas en Cámaras, integración con Word y resúmenes automatizados."
                              : isTrialActive
                                ? "Suscribite ahora para continuar con búsquedas ilimitadas, integración con Word y resúmenes automatizados."
                                : "Accedé a búsquedas ilimitadas, integración con Word y resúmenes automatizados de fallos."}
                          </p>
                        </div>
                        <span className="text-sm font-medium text-primary whitespace-nowrap">
                          Ver planes →
                        </span>
                      </div>
                    </div>
                  );
                })()}

              <SearchInterface
                ref={searchInterfaceRef}
                onResults={handleResults}
                onLoadingChange={setIsSearching}
                onTabChange={setActiveTab}
                onFilterTermsChange={setFilterTerms}
              />
              
              {/* Semantic Search Callout for Tutorial */}
              <SemanticSearchCallout />
              
              {(searchResults.length > 0 || isSearching) && (
                <SearchResults
                  results={searchResults}
                  collection={currentCollection}
                  isLoading={isSearching}
                  onSearchDoctrina={handleSearchDoctrina}
                  onSearchObjeto={handleSearchObjeto}
                  activeTab={activeTab}
                  highlightTerms={filterTerms}
                />
              )}
            </div>
          )}
        </div>

        <ChangelogModal open={showChangelog} onOpenChange={setShowChangelog} />
        <TutorialOverlay />
        <WordAddinModal isOpen={showWordAddinModal} onClose={() => setShowWordAddinModal(false)} />
        <TutorialCompletionModal isOpen={showCompletionModal} onClose={() => setShowCompletionModal(false)} />
      </div>
    </>
  );
};

export default Index;
